import type { LineListIn } from "@api/generated/model";
import { useTrial } from "@app/trials";
import { LinePicker } from "@features/genealogy/GenotypeParts";
import { keyParam } from "@features/genealogy/genotype";
import { SavedQuerySelect } from "@features/queries/SavedQuerySelect";
import { hint } from "@i18n";
import { Button, Select } from "@mantine/core";
import { RemovableChip } from "@ui/RemovableChip";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const pickedSchema = z.object({ paz: z.number().int(), sid: z.number().int(), name: z.string() });
export type Picked = z.infer<typeof pickedSchema>;

/** A genotype list as a link carries it: trials of a season (kept with them, so a shared link lists the same plots),
 *  a saved query, picked lines. */
export type LineListValue = { ev?: number; trials?: string[]; q?: number; lines?: Picked[] };

export const lineListEmpty = (v: LineListValue) => !v.trials?.length && v.q == null && !v.lines?.length;

/** The list as the API takes it. */
export function lineListBody(season: number, v: LineListValue): LineListIn {
  return { ev: season, trials: v.trials ?? [], query: v.q ?? null, lines: (v.lines ?? []).map((p) => ({ paz: p.paz, sid: p.sid })) };
}

/** The genotype list of Felmenők keresése and Génkereső (the legacy "Genotype list"): a season's trials, a saved
 *  query's lines (the legacy Query closed with "Genotype list") and lines picked one by one, each removable. */
export function LineListCard(props: {
  season: number | undefined;
  value: LineListValue;
  onChange: (patch: LineListValue) => void;
  title?: string;
}) {
  const { t } = useTranslation();
  const { trials } = useTrial(props.season, undefined, undefined);
  const chosen = props.value.trials ?? [];
  const lines = props.value.lines ?? [];
  const add = (p: Picked) => !lines.some((x) => keyParam(x) === keyParam(p)) && props.onChange({ lines: [...lines, p] });
  return (
    <section className="card pad stack grow" style={{ gap: 8, minWidth: 320, flexBasis: 0 }}>
      <span className="sec">{props.title ?? t("ancestors.lineList")}</span>
      <div className="hstack" style={{ gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Select
          placeholder={hint("searchSelect")}
          size="xs"
          w={200}
          searchable
          label={t("ancestors.addTrial")}
          value={null}
          onChange={(v) => v && !chosen.includes(v) && props.onChange({ trials: [...chosen, v], ev: props.season })}
          data={trials.map((x) => x.kis).filter((x) => !chosen.includes(x))}
        />
        <SavedQuerySelect
          w={240}
          label={t("ancestors.addQuery")}
          description={t("ancestors.queryHelp")}
          value={props.value.q ?? null}
          onChange={(id) => props.onChange({ q: id ?? undefined })}
        />
      </div>
      <LinePicker label={t("ancestors.addLine")} onPick={(k, name) => add({ ...k, name })} />
      <div className="chips">
        {chosen.map((x) => (
          <RemovableChip
            key={`t:${x}`}
            tone="g"
            removeLabel={t("ancestors.remove", { name: x })}
            onRemove={() => props.onChange({ trials: chosen.filter((y) => y !== x) })}
          >
            {t("ancestors.trialChip", { kis: x })}
          </RemovableChip>
        ))}
        {lines.map((x) => (
          <RemovableChip
            key={keyParam(x)}
            removeLabel={t("ancestors.remove", { name: x.name })}
            onRemove={() => props.onChange({ lines: lines.filter((y) => y !== x) })}
          >
            {x.name}
          </RemovableChip>
        ))}
      </div>
      {!lineListEmpty(props.value) && (
        <Button
          size="compact-xs"
          variant="subtle"
          style={{ alignSelf: "flex-end" }}
          onClick={() => props.onChange({ ev: undefined, trials: undefined, q: undefined, lines: undefined })}
        >
          {t("ancestors.clearList")}
        </Button>
      )}
    </section>
  );
}
